import { useReactFlow, Handle, Position, NodeResizer } from "@xyflow/react";
import { Button, Input, Select } from "antd";
import { CloseOutlined, CopyOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import NodeNameInput from "./NodeNameInput";

const COLOR = "#8b5cf6";

const FIELD_OPTIONS = [
  { label: "First Name",       value: "firstname" },
  { label: "Last Name",        value: "lastname" },
  { label: "Email",            value: "email" },
  { label: "Phone",            value: "phone" },
  { label: "Company",          value: "company" },
  { label: "Designation",      value: "designation" },
  { label: "Status",           value: "status" },
  { label: "Notes",            value: "notes" },
  { label: "Website",          value: "website" },
  { label: "LinkedIn",         value: "linkedin" },
  { label: "Instagram",        value: "instagram" },
  { label: "Custom Variable",  value: "_custom" },
];

const OPERATOR_OPTIONS = [
  { label: "equals",           value: "eq" },
  { label: "not equals",       value: "ne" },
  { label: "contains",         value: "contains" },
  { label: "not contains",     value: "notContains" },
  { label: "starts with",      value: "startsWith" },
  { label: "ends with",        value: "endsWith" },
  { label: "greater than",     value: "gt" },
  { label: "less than",        value: "lt" },
  { label: "greater or equal", value: "gte" },
  { label: "less or equal",    value: "lte" },
  { label: "is empty",         value: "empty" },
  { label: "is not empty",     value: "notEmpty" },
];

const NO_VALUE_OPS = ["empty", "notEmpty"];

const newCondition = () => ({ field: "firstname", operator: "eq", customField: "", value: "" });
const newBranch = (index) => ({
  key: `branch_${index}_${Date.now()}`,
  label: `Branch ${index + 1}`,
  logic: "ALL",
  conditions: [newCondition()],
});

const ConditionNode = ({ data, selected, id }) => {
  const { deleteElements } = useReactFlow();
  const branches = data.branches || [];

  const updateBranches = (next) => data.onChange?.({ ...data, branches: next });

  const addBranch = () => updateBranches([...branches, newBranch(branches.length)]);

  const removeBranch = (bi) => updateBranches(branches.filter((_, i) => i !== bi));

  const patchBranch = (bi, patch) => updateBranches(branches.map((b, i) => i === bi ? { ...b, ...patch } : b));

  const addCondition = (bi) => {
    const b = branches[bi];
    patchBranch(bi, { conditions: [...b.conditions, newCondition()] });
  };

  const removeCondition = (bi, ci) => {
    const b = branches[bi];
    patchBranch(bi, { conditions: b.conditions.filter((_, i) => i !== ci) });
  };

  const patchCondition = (bi, ci, patch) => {
    const b = branches[bi];
    patchBranch(bi, { conditions: b.conditions.map((c, i) => i === ci ? { ...c, ...patch } : c) });
  };

  // Each branch gets a named source handle + a default handle at bottom
  const handleIds = [...branches.map((b) => b.key), "default"];

  return (
    <div style={{
      background: "#fff",
      border: `2px solid ${selected ? COLOR : "#e5e7eb"}`,
      borderRadius: 10,
      padding: 14,
      minWidth: 320,
      maxWidth: 400,
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      position: "relative",
      width: "100%",
      height: "100%",
      boxSizing: "border-box",
    }}>
      <NodeResizer minWidth={240} minHeight={80} isVisible={selected} color={COLOR} />
      <Handle type="target" position={Position.Left} style={{ background: COLOR }} />

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 16 }}>🔀</span>
        <strong style={{ fontSize: 13 }}>Condition / Branch</strong>
        <Button type="text" size="small" icon={<CopyOutlined />}
          onClick={() => data.onDuplicate?.()}
          style={{ marginLeft: "auto", color: "#9ca3af" }} />
        <Button type="text" size="small" icon={<CloseOutlined />}
          onClick={() => deleteElements({ nodes: [{ id }] })}
          style={{ color: "#9ca3af" }} />
      </div>

      <div className="nodrag" onPointerDown={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <NodeNameInput data={data} />
        {branches.map((branch, bi) => (
          <div key={branch.key} style={{
            border: "1px solid #e5e7eb",
            borderRadius: 7,
            padding: 8,
            marginBottom: 8,
            background: "#f9fafb",
            position: "relative",
          }}>
            {/* Branch header */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
              <Input
                value={branch.label}
                onChange={(e) => patchBranch(bi, { label: e.target.value })}
                size="small"
                style={{ flex: 1, fontWeight: 600 }}
                placeholder={`Branch ${bi + 1}`}
              />
              <Select
                value={branch.logic}
                onChange={(v) => patchBranch(bi, { logic: v })}
                size="small"
                style={{ width: 70 }}
                options={[{ label: "ALL", value: "ALL" }, { label: "ANY", value: "ANY" }]}
              />
              <Button type="text" size="small" icon={<DeleteOutlined />}
                onClick={() => removeBranch(bi)}
                style={{ color: "#ef4444", padding: "0 4px" }} />
            </div>

            {/* Conditions */}
            {branch.conditions.map((cond, ci) => (
              <div key={ci} style={{ display: "flex", gap: 4, alignItems: "center", marginBottom: 4 }}>
                {ci > 0 && (
                  <span style={{ fontSize: 10, color: "#6b7280", minWidth: 28, textAlign: "center" }}>
                    {branch.logic}
                  </span>
                )}
                <Select
                  value={cond.field}
                  onChange={(v) => patchCondition(bi, ci, { field: v, customField: "" })}
                  size="small"
                  style={{ flex: 1, minWidth: 90 }}
                  options={FIELD_OPTIONS}
                />
                {cond.field === "_custom" && (
                  <Input
                    value={cond.customField || ""}
                    onChange={(e) => patchCondition(bi, ci, { customField: e.target.value })}
                    size="small"
                    placeholder="var name"
                    style={{ flex: 1 }}
                  />
                )}
                <Select
                  value={cond.operator}
                  onChange={(v) => patchCondition(bi, ci, { operator: v, value: "" })}
                  size="small"
                  style={{ flex: 1, minWidth: 100 }}
                  options={OPERATOR_OPTIONS}
                />
                {!NO_VALUE_OPS.includes(cond.operator) && (
                  <Input
                    value={cond.value}
                    onChange={(e) => patchCondition(bi, ci, { value: e.target.value })}
                    size="small"
                    placeholder="value"
                    style={{ flex: 1, minWidth: 60 }}
                  />
                )}
                <Button type="text" size="small" icon={<DeleteOutlined />}
                  onClick={() => removeCondition(bi, ci)}
                  style={{ color: "#9ca3af", padding: "0 2px", flexShrink: 0 }} />
              </div>
            ))}

            <Button type="dashed" size="small" icon={<PlusOutlined />}
              onClick={() => addCondition(bi)}
              style={{ width: "100%", marginTop: 2, fontSize: 11, borderColor: COLOR, color: COLOR }}>
              Add Condition
            </Button>

            {/* Named source handle per branch */}
            <Handle
              type="source"
              position={Position.Right}
              id={branch.key}
              style={{
                background: COLOR,
                top: "50%",
                right: -8,
              }}
            />
          </div>
        ))}

        <Button type="dashed" size="small" icon={<PlusOutlined />}
          onClick={addBranch}
          style={{ width: "100%", borderColor: COLOR, color: COLOR, marginBottom: 8 }}>
          Add Branch
        </Button>

        <div style={{ fontSize: 10, color: "#6b7280", padding: "4px 6px", background: "#f3f4f6", borderRadius: 4 }}>
          Default → connects to fallback node when no branch matches
        </div>
      </div>

      {/* Default fallback handle at bottom */}
      <Handle
        type="source"
        position={Position.Right}
        id="default"
        style={{ background: "#9ca3af", bottom: 20, top: "auto" }}
      />
    </div>
  );
};

export default ConditionNode;
